import React from 'react';
import { Trash2 } from 'lucide-react';
import type { BoardMember } from 'types/kanban';
import { getMemberColor, getInitials } from '../kanbanUtils';
import {
  useCurrentUserAvatar,
  isCurrentUser as isCurrentUserAvatar,
} from '../useCurrentUserAvatar';
import styles from './BoardMembersPanel.module.scss';

export interface BoardMembersPanelProps {
  show: boolean;
  onClose: () => void;
  boardMembers: BoardMember[];
  addMemberEmail: string;
  onAddMemberEmailChange: (value: string) => void;
  addingMember: boolean;
  onLinkUser: (email: string) => void;
  onRequestRemoveMember: (member: BoardMember) => void;
}

export default function BoardMembersPanel({
  show,
  onClose,
  boardMembers,
  addMemberEmail,
  onAddMemberEmailChange,
  addingMember,
  onLinkUser,
  onRequestRemoveMember,
}: BoardMembersPanelProps) {
  const { currentUserIds, profilePictureUrl } = useCurrentUserAvatar();
  if (!show) return null;

  return (
    <div
      className={styles.panelBackdrop}
      onClick={onClose}
      role="presentation"
      aria-hidden
    >
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Board members"
      >
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Board members</h2>
          <button
            type="button"
            className={styles.panelClose}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.addMemberRow}>
            <div className={styles.addMemberInputGroup}>
              <label
                htmlFor="board-link-email"
                className={styles.addMemberLabel}
              >
                Add user by email
              </label>
              <input
                id="board-link-email"
                type="email"
                className={styles.addMemberInput}
                placeholder="user@example.com"
                value={addMemberEmail}
                onChange={(e) => onAddMemberEmailChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = addMemberEmail.trim();
                    if (!trimmed || addingMember) return;
                    void onLinkUser(trimmed);
                  }
                }}
                aria-label="User email to add to board"
                disabled={addingMember}
              />
            </div>
            <button
              type="button"
              className={styles.addMemberBtn}
              onClick={() => void onLinkUser(addMemberEmail)}
              disabled={!addMemberEmail.trim() || addingMember}
            >
              {addingMember ? 'Linking…' : 'Link User'}
            </button>
          </div>
        </div>
        <div className={styles.panelBody}>
          {boardMembers.length === 0 ? (
            <p className={styles.emptyMessage}>
              No members on this board. Add one above.
            </p>
          ) : (
            <ul className={styles.membersList}>
              {boardMembers.map((member) => {
                const memberKey = member.userId ?? member.email ?? member.id;
                const resolvedAvatarUrl =
                  member.profilePictureUrl ??
                  (isCurrentUserAvatar(currentUserIds, member.userId)
                    ? profilePictureUrl
                    : null);
                return (
                  <li key={memberKey} className={styles.membersItem}>
                    {resolvedAvatarUrl ? (
                      <img
                        src={resolvedAvatarUrl}
                        alt=""
                        className={styles.memberAvatarImg}
                        title={member.name}
                        aria-hidden
                      />
                    ) : (
                      <span
                        className={styles.memberAvatar}
                        style={{
                          background: getMemberColor(memberKey),
                          color: 'white',
                        }}
                        title={member.name}
                      >
                        {getInitials(member.name)}
                      </span>
                    )}
                    <span className={styles.memberName}>{member.name}</span>
                    <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => onRequestRemoveMember(member)}
                      aria-label={`Remove ${member.name} from board`}
                    >
                      <Trash2 size={14} />
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
